<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { ref, watch } from 'vue';

import KSwitchField from '@/components/fields/KSwitchField.vue';
import { fetchPutJobCanSchedule } from '@/helpers/fetchers';

export interface Props {
  job?: ReadonlyDeep<
    Pick<
      openapi.JobDetailedOpenapi,
      'stops' | 'rockhopper_id' | 'rockhopper_date'
    >
  >;
  refreshDetailedJob?: () => Promise<void>;
}
const props = withDefaults(defineProps<Props>(), {
  job: undefined,
  refreshDetailedJob: undefined,
});

const toggle = ref(false);
const isLoading = ref(false);

watch(
  () => props.job?.stops[0].can_schedule,
  (newValue, oldValue) => {
    if (!props.job) return;
    toggle.value = props.job.stops[0].can_schedule;
  },
  { immediate: true },
);

async function handleUpdateModelValue(value: boolean): Promise<void> {
  if (isLoading.value || !props.job) {
    toggle.value = !value;
    return;
  }
  isLoading.value = true;
  await fetchPutJobCanSchedule({
    can_schedule: value,
    job_id: props.job.rockhopper_id,
    job_date: props.job.rockhopper_date,
  })
    .then(async () => {
      if (props.refreshDetailedJob) {
        await props.refreshDetailedJob();
      }
    })
    .finally(() => {
      isLoading.value = false;
      toggle.value = !!props.job?.stops[0].can_schedule;
    });
}
</script>

<template>
  <article
    :class="$style.scheduleSwitchDetailedJobPageUnit"
    data-testid="scheduleSwitchDetailedJobPageUnit"
  >
    <KSwitchField
      v-model="toggle"
      :class="$style.switchField"
      :label="toggle ? 'Can be scheduled' : 'Cannot be scheduled'"
      hide-details
      :disabled="!props.job"
      :loading="isLoading"
      inset
      priority="success"
      is-small
      is-label-color
      @update:model-value="handleUpdateModelValue"
    />
  </article>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.scheduleSwitchDetailedJobPageUnit {
  border: 1px solid colors.$color-green-800;
  background-color: colors.$color-gray-50;
  display: inline-block;
  padding: 0 8px;
}
  .switchField {
  }
</style>
